import React, { useMemo, useState } from "react";
import { timeSlots, services, contactInfo } from "../mock";
import { CalendarClock, User, Mail, Phone, Loader2, CheckCircle2 } from "lucide-react";
import { useToast } from "../hooks/use-toast";

export default function AppointmentSection() {
  const { toast } = useToast();
  const num1 = useMemo(() => Math.floor(Math.random() * 40) + 5, []);
  const num2 = useMemo(() => Math.floor(Math.random() * 50) + 10, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    matter: services[0]?.slug || "",
    message: "",
    captcha: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const submit = (e) => {
    e.preventDefault();
    if (Number(form.captcha) !== num1 + num2) {
      toast({
        title: "Verification failed",
        description: `Please enter the correct sum of ${num1} + ${num2}.`,
        variant: "destructive",
      });
      return;
    }
    setLoading(true);
    setTimeout(() => {
      const stored = JSON.parse(localStorage.getItem("pg_appointments") || "[]");
      stored.unshift({ ...form, id: Date.now() });
      localStorage.setItem("pg_appointments", JSON.stringify(stored));
      setLoading(false);
      setDone(true);
      toast({
        title: "Appointment Requested",
        description: `Thank you ${form.name}. Our team will confirm your slot on ${form.date} at ${form.time}.`,
      });
      setTimeout(() => setDone(false), 3200);
      setForm({
        name: "", email: "", phone: "", date: "", time: "",
        matter: services[0]?.slug || "", message: "", captcha: "",
      });
    }, 1100);
  };

  const today = new Date().toISOString().split("T")[0];

  return (
    <section
      id="appointment"
      className="py-24 bg-cover bg-center relative"
      style={{
        backgroundImage:
          "linear-gradient(rgba(14, 27, 52, 0.94), rgba(14, 27, 52, 0.96)), url('https://images.unsplash.com/photo-1568092806323-8ec13dfa9b92?auto=format&fit=crop&w=1920&q=80')",
      }}
    >
      <div className="container-x grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 text-white">
          <span className="text-[#c9a961] uppercase tracking-[0.35em] text-xs font-semibold">Book a Consultation</span>
          <h2 className="font-serif-display text-4xl md:text-5xl mt-3 leading-tight gold-underline">
            Fix an Appointment
          </h2>
          <p className="text-neutral-300 mt-6 leading-relaxed">
            Schedule a confidential consultation with our team. All appointments are subject to confirmation.
            Minimum consultation fees Rs. 2,000/-.
          </p>
          <ul className="mt-8 space-y-4 text-sm">
            <li className="flex items-center gap-3"><Phone size={16} className="text-[#c9a961]" /> {contactInfo.phone}</li>
            <li className="flex items-center gap-3"><Mail size={16} className="text-[#c9a961]" /> {contactInfo.email}</li>
            <li className="flex items-center gap-3"><CalendarClock size={16} className="text-[#c9a961]" /> Mon-Sat 10am to 10pm</li>
          </ul>
        </div>

        <div className="lg:col-span-3">
          <form onSubmit={submit} className="bg-white p-8 md:p-10 rounded-sm shadow-2xl">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Full Name *" icon={User}>
                <input required name="name" value={form.name} onChange={onChange} type="text" placeholder="Your name" className="input-plain" />
              </Field>
              <Field label="Phone *" icon={Phone}>
                <input required name="phone" value={form.phone} onChange={onChange} type="tel" placeholder="+91 98xxxxxxxx" className="input-plain" />
              </Field>
              <Field label="Email *" icon={Mail}>
                <input required name="email" value={form.email} onChange={onChange} type="email" placeholder="you@email.com" className="input-plain" />
              </Field>
              <Field label="Nature of Matter *">
                <select required name="matter" value={form.matter} onChange={onChange} className="input-plain bg-white">
                  {services.map((s) => (
                    <option key={s.slug} value={s.slug}>{s.title}</option>
                  ))}
                </select>
              </Field>
              <Field label="Preferred Date *">
                <input required min={today} name="date" value={form.date} onChange={onChange} type="date" className="input-plain" />
              </Field>
              <Field label="Preferred Time *">
                <select required name="time" value={form.time} onChange={onChange} className="input-plain bg-white">
                  <option value="">Select a time slot</option>
                  {timeSlots.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Brief Description">
                  <textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Briefly describe your matter (confidential)" className="input-plain resize-none" />
                </Field>
              </div>
              <div className="md:col-span-2 flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <label className="block text-xs uppercase tracking-widest text-[#0e1b34] font-semibold mb-2">
                    Type the above number: {num1} + {num2} =
                  </label>
                  <input required name="captcha" value={form.captcha} onChange={onChange} type="number" placeholder="Answer" className="input-plain" />
                </div>
                <button type="submit" disabled={loading} className="btn-gold justify-center flex-1">
                  {loading ? <><Loader2 size={16} className="animate-spin" /> Sending...</> :
                    done ? <><CheckCircle2 size={16} /> Submitted</> :
                    <>Request Appointment</>}
                </button>
              </div>
            </div>
            <p className="text-[11px] text-neutral-500 mt-5">
              * Subject to Confirmation. * Minimum Consultation Fee: Rs. 2,000/-
            </p>
          </form>
        </div>
      </div>

      <style>{`
        .input-plain {
          width: 100%;
          border: 1px solid #e5e5e5;
          border-radius: 2px;
          padding: 12px 14px;
          font-size: 14px;
          color: #0e1b34;
          background: #fafafa;
          transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
          outline: none;
        }
        .input-plain:focus {
          border-color: #c9a961;
          box-shadow: 0 0 0 3px rgba(201, 169, 97, 0.15);
          background: #ffffff;
        }
      `}</style>
    </section>
  );
}

function Field({ label, icon: Icon, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#0e1b34] font-semibold mb-2">
        {label}
      </label>
      <div className="relative">{children}</div>
    </div>
  );
}
